import { ChangeDetectorRef, Component, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { LocalDate } from '../../../core/model/local-date';
import { ReportService } from '../../service/data/report.service';
import { SelectionItem } from '../../../core/component/select/select.component';
import { getMonthName } from '../../../core/helper/month';
import { Subscription } from 'rxjs';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { CompanyService } from '../../../rent/service/data/company.service';
import { PropertyService } from '../../../rent/service/data/property.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { AdjustmentKind, RentalObjectCategory } from '../../../rent/model/const/enums';
import { ArticleGroupService } from '../../../rent/service/data/article-group.service';
import { getTranslation } from '../../../core/helper/translation';
import { ProfileService } from '../../../common/service/data/profile.service';
import { ArticleService } from '../../../rent/service/data/article.service';
import ListViewColumn from '../../../core/model/list-view-column';
import { LanguageService } from '../../../core/service/language.service';
import { canSubmitReportForm } from '../../utility/report-form-validation';

@Component({
  selector: 'app-rent-roll',
  templateUrl: './rent-roll.component.html',
  styleUrls: ['./rent-roll.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class RentRollComponent implements OnDestroy, OnInit {
  constructor(
    private _cdf: ColumnDefinitionFactory,
    private _dataService: ReportService,
    private _nativeWindow: NativeWindowService,
    private _companyService: CompanyService,
    private _propertyService: PropertyService,
    private _profileService: ProfileService,
    private _navigationService: NavigationService,
    private _articleGroupService: ArticleGroupService,
    private readonly _languageService: LanguageService,
    private _articleService: ArticleService,
    private _cdr: ChangeDetectorRef,
    fb: FormBuilder,
    fcf: FormControlExFactory
  ) {
    this.form = fb.group({
      companyId: fcf.create('COMMON.COMPANY', null),
      propertyId: fcf.create('COMMON.PROPERTY', null),
      year: fcf.create('REPORT.DISPLAY_YEAR', LocalDate.today().year),
      month: fcf.create('COMMON.MONTH', LocalDate.today().month),
      personalData: fcf.create('COMMON.DISPLAY_PERSONAL_DATA', 'no'),
      rentalId: fcf.create('RENTAL_OBJECT_FORM.RENTAL_OBJECT_NUMBER_LABEL', null),
      city: fcf.create('RENTAL_OBJECT_LIST.CITY_COLUMN', null),
      rentalObjectCategory: fcf.create('COMMON.RENTAL_OBJECT_CATEGORY', null),
      onlyIncludeContractualRentArticles: fcf.create('REPORT.INCLUDE_ARTICLES', 'all'),
    });

    const currentYear = new Date().getFullYear();
    for (let offset = -3; offset <= 10; offset++)
      this.yearSelection.push({ name: (currentYear + offset).toString(), value: currentYear + offset });
  }

  private _sub = new Subscription();
  columns: ListViewColumn[];
  get excelContext() {
    return 'RentRoll';
  }

  onColumnChange(columns: ListViewColumn[]) {
    this.columns = columns;
    this._cdr.detectChanges();
  }

  get selectedColumns(): ListViewColumn[] {
    return this.columns?.filter(x => x.selected);
  }

  ngOnInit() {
    this._sub.add(
      this._companyService.getList().subscribe(data => {
        this.companies = [{ name: 'ALL_TEXT', value: null }];
        data.forEach(item => {
          this.companies.push({ name: item.toString(), value: item.id });
        });

        if (this.companies.length >= 2) {
          this.form.controls['companyId'].setValue(this.companies[1].value);
        }
      })
    );

    this.createColumnDefinitions();

    this._sub.add(
      this._propertyService.getList().subscribe(data => {
        this.realEstateProperties = [{ name: 'ALL_TEXT', value: null }];
        data.forEach(item => {
          this.realEstateProperties.push({ name: item.toString(), value: item.id });
        });
      })
    );

    this.personalDataSelection = [{ name: 'NO_TEXT', value: 'no' }];
    this.personalDataSelection.push({ name: 'YES_TEXT', value: 'yes' });

    this.onlyIncludeContractualRentArticlesSelection = [{ name: 'REPORT.ALL', value: 'all' }];
    this.onlyIncludeContractualRentArticlesSelection.push({
      name: 'REPORT.INCLUDED_IN_CONTRACTUAL_RENT',
      value: 'onlyincludecontractualrent',
    });

    this.rentalObjectCategorySelection = [{ name: 'ALL_TEXT', value: null }];
    this.rentalObjectCategorySelection.push({ name: 'CONST.RENTAL_OBJECT_CATEGORY_DWELLING', value: RentalObjectCategory.Dwelling });
    this.rentalObjectCategorySelection.push({ name: 'CONST.RENTAL_OBJECT_CATEGORY_PREMISES', value: RentalObjectCategory.Premises });
    this.rentalObjectCategorySelection.push({ name: 'CONST.RENTAL_OBJECT_CATEGORY_PARKING', value: RentalObjectCategory.Parking });
    this.rentalObjectCategorySelection.push({ name: 'CONST.RENTAL_OBJECT_CATEGORY_OTHER', value: RentalObjectCategory.Other });
  }

  private createColumnDefinitions() {
    let columnDefinitions = [
      this._cdf.create('COMMON.COMPANY', 'company', ColumnVisibility.mandatory),
      this._cdf.create('COMMON.PROPERTY', 'propertyName'),
      this._cdf.create('PROPERTY.NUMBER', 'propertyNumber', ColumnVisibility.optional),
      this._cdf.create('PROPERTY.COST_PLACE', 'costPlace', ColumnVisibility.optional),
      this._cdf.create('COMMON.BUILDING', 'buildingName', ColumnVisibility.optional),
      this._cdf.create('RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId'),
      this._cdf.create('COMMON.ADDRESS', 'address'),
      this._cdf.create('COMMON.RENTAL_OBJECT_CATEGORY', 'rentalObjectCategoryText', ColumnVisibility.optional),
      this._cdf.create('RENTAL_OBJECT_TYPE_LIST.NAME_COLUMN', 'rentalObjectType'),
      this._cdf.createDate('VACANT_RENTAL_OBJECT_LIST.VACANT_PERIOD_TO', 'vacantTo', ColumnVisibility.optional),
      this._cdf.create('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', 'leaseAgreementNumber', ColumnVisibility.optional),
      this._cdf.create('LEASE_AGREEMENT.TENANT_ONE', 'tenantOneName', ColumnVisibility.optional),
      this._cdf.create('LEASE_AGREEMENT.TENANT_ONE_CIVICNUMBER', 'tenantOneContactIdentification', ColumnVisibility.optional),
      this._cdf.createDate('LEASE_AGREEMENT.FROM_DATE', 'leaseAgreementFrom', ColumnVisibility.optional),
      this._cdf.createDate('LEASE_AGREEMENT.TO_DATE', 'leaseAgreementLastBillingDate', ColumnVisibility.optional),
      this._cdf.create('LEASE_AGREEMENT_NOTICE_PERIOD_FORM.LANDLORD_LABEL', 'noticePeriodLandlord', ColumnVisibility.optional),
      this._cdf.create('LEASE_AGREEMENT_NOTICE_PERIOD_FORM.PROLONGATION_TIME_LABEL', 'prolongationTime', ColumnVisibility.optional),
      this._cdf.createDate('LEASE_AGREEMENT.VALID_CONTRACT_PERIOD_TO', 'validContractPeriodToLandlord', ColumnVisibility.optional),
      this._cdf.create('LEASE_AGREEMENT_NOTICE_PERIOD_FORM.TENANT_LABEL', 'noticePeriodTenant', ColumnVisibility.optional),
      this._cdf.createDate('LEASE_AGREEMENT.VALID_CONTRACT_PERIOD_TO_TENANT', 'validContractPeriodToTenant', ColumnVisibility.optional),
      this._cdf.createDate('VACANT_RENTAL_OBJECT_LIST.VACANT_PERIOD_FROM', 'nextVacancyFrom', ColumnVisibility.optional),
      this._cdf.createNumber('RENTAL_OBJECT_FORM.OBJECT_AREA', 'surface', { visibility: ColumnVisibility.default, totalSum: true }),
      this._cdf.createNumber('LEASE_AGREEMENT.LEASE_AGREEMENT_AREA', 'areaFromLeaseAgreement', {
        visibility: ColumnVisibility.optional,
        totalSum: true,
      }),
      this._cdf.createMoney('REPORT.RENTAL_VALUE', 'rentalValue'),
      this._cdf.createMoney('REPORT.RENTAL_VALUE_PER_SQM', 'rentalValuePerSqM', ColumnVisibility.optional),
      this._cdf.createMoney('REPORT.TOTALS_OBJECT_YEAR', 'baseYearTotalAmount'),
      this._cdf.createMoney('REPORT.TOTALS_OBJECT_YEAR_PER_SQM', 'baseYearTotalAmountPerSqM', ColumnVisibility.optional),
      this._cdf.createMoney('REPORT.TOTALS_AGREEMENT_YEAR', 'agreementYearTotalAmount'),
      this._cdf.createMoney('REPORT.TOTALS_AGREEMENT_YEAR_PER_SQM', 'agreementYearTotalAmountPerSqM', ColumnVisibility.optional),
      this._cdf.createMoney('REPORT.RENTAL_VALUE_LEASE_AGREEMENT', 'agreementYearRent', ColumnVisibility.optional),
      this._cdf.createMoney('REPORT.RENTAL_VALUE_RENTAL_OBJECT', 'baseYearRentOnlyRent', ColumnVisibility.optional),
      this._cdf.createMoney('REPORT.RENT_SELECTED_MONTH', 'agreementMonthRent', ColumnVisibility.optional),
      this._cdf.createMoney('REPORT.DISCOUNT_YEAR1', 'agreementYearRentDiscountYear1'),
      this._cdf.createMoney('REPORT.DISCOUNT_YEAR2', 'agreementYearRentDiscountYear2'),
      this._cdf.createMoney('REPORT.DISCOUNT_YEAR3', 'agreementYearRentDiscountYear3', ColumnVisibility.optional),
      this._cdf.createMoney('REPORT.DISCOUNT_YEAR4', 'agreementYearRentDiscountYear4', ColumnVisibility.optional),
      this._cdf.createMoney('REPORT.DISCOUNT_YEAR5', 'agreementYearRentDiscountYear5', ColumnVisibility.optional),
      this._cdf.createMoney('LEASE_AGREEMENT_ROW.INDEX_RENT_BASE', 'agreementIndexBaseYearRent', ColumnVisibility.optional),
      this._cdf.createMoney('LEASE_AGREEMENT_ROW.INDEX_RENT_CURRENT', 'agreementIndexYearRent', ColumnVisibility.optional),
      this._cdf.create('LEASE_AGREEMENT_ROW.BASE_INDEX_YEAR', 'baseIndexYearFirst', ColumnVisibility.optional),
      this._cdf.create('LEASE_AGREEMENT_ROW.BASE_INDEX_MONTH', 'baseIndexMonthFirst', ColumnVisibility.optional),
      this._cdf.create('LEASE_AGREEMENT_ROW.INDEX_CLAUSE', 'indexClauseFirst', ColumnVisibility.optional),
      this._cdf.createNumber('LEASE_AGREEMENT_ROW.INDEX_SHARE', 'indexShareFirst', ColumnVisibility.optional),
    ];

    this._sub.add(
      this._articleGroupService.getList().subscribe(groups => {
        let tran = this._languageService.getCurrentTranslations();
        let baseRentText = getTranslation(tran, 'COMMON.RENTAL_OBJECT');
        let rentSelectedMonthText = getTranslation(tran, 'REPORT.RENT_SELECTED_MONTH');
        let agreementYearRentText = getTranslation(tran, 'CONST.RENTAL_CHANGES_TYPE_AGREEMENT');
        let yearText = getTranslation(tran, 'COMMON.YEAR').toLowerCase();

        for (let group of groups) {
          columnDefinitions.push(
            this._cdf.createMoney(
              baseRentText + ' ' + group.name + ' ' + yearText,
              `baseYearRentPerArticleGroupId.${group.id}`,
              ColumnVisibility.optional
            ),
            this._cdf.createMoney(
              agreementYearRentText + ' ' + group.name + ' ' + yearText,
              `agreementYearRentPerArticleGroupId.${group.id}`,
              ColumnVisibility.optional
            ),
            this._cdf.createMoney(
              rentSelectedMonthText + ' ' + group.name,
              `agreementMonthRentPerArticleGroupId.${group.id}`,
              ColumnVisibility.optional
            )
          );
        }

        columnDefinitions.push(
          this._cdf.createMoney('LEASE_AGREEMENT.DEPOSIT_AMOUNT_LEASE_AGREEMENT', 'leaseAgreeementDepositAmount', ColumnVisibility.optional)
        );

        this._sub.add(
          this._profileService.getConfig().subscribe(res => {
            if (res.useBrf) {
              columnDefinitions.push(this._cdf.create('COOPERATIVE_HOUSING.SHARE', 'condominiumShare', ColumnVisibility.optional));
            }
          })
        );

        this.columnDefinitions = columnDefinitions;
        this.createExcelColumnDefinitions();
      })
    );
  }

  ngOnDestroy(): void {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
    if (this._sub) {
      this._sub.unsubscribe();
    }
  }

  columnDefinitions: ColumnDefinition[] = [];
  excelColumnDefinitions: ColumnDefinition[] = [];
  yearSelection: { name: string; value: number }[] = [];
  companies: { name: string; value: string }[];
  realEstateProperties: { name: string; value: string }[];
  personalDataSelection: { name: string; value: string }[];
  onlyIncludeContractualRentArticlesSelection: { name: string; value: string }[];
  rentalObjectCategorySelection: { name: string; value: number }[];

  createExcelColumnDefinitions() {
    this.excelColumnDefinitions = [...this.columnDefinitions];
    this.excelColumnDefinitions.push(
      this._cdf.create('RENT_ROLL.APARTMENTNUMBER', 'apartmentNumber', ColumnVisibility.optional),
      this._cdf.create('ADDRESS_FORM.POSTAL_CODE_LABEL', 'postalCode', ColumnVisibility.optional),
      this._cdf.create('ADDRESS_FORM.CITY_LABEL', 'city', ColumnVisibility.optional),
      this._cdf.create('VACANT_RENTAL_OBJECT_LIST.VACANT_TEXT', 'vacant', ColumnVisibility.optional),
      this._cdf.create('RENT_ROLL.VAT_REGISTERED', 'vatRegistered', ColumnVisibility.optional),
      this._cdf.create('RENTAL_OBJECT.BLOCKED_FROM', 'blockedFrom', ColumnVisibility.optional),
      this._cdf.create('RENTAL_OBJECT.BLOCKED_TO', 'blockedTo', ColumnVisibility.optional),
      this._cdf.create('RENTAL_OBJECT.BLOCKING_REASON', 'blockingReason', ColumnVisibility.optional),
      this._cdf.create('RENT_ROLL.RENTAL_OBJECT_NOTE', 'rentalObjectNote', ColumnVisibility.optional),
      this._cdf.create('RENT_ROLL.LEASE_AGREEMENT_NOTE', 'leaseAgreementNote', ColumnVisibility.optional)
    );
    this.addFixedPercentageRowsColumns();
    this.addIndexInformationColumns();
  }

  private addFixedPercentageRowsColumns() {
    const tran = this._languageService.getCurrentTranslations();
    const increaseText = getTranslation(tran, 'LEASE_AGREEMENT_ROW.FIXED_PERCENTAGE_INCREASE');
    const nextDateText = getTranslation(tran, 'LEASE_AGREEMENT_ROW.FIXED_PERCENTAGE_INCREASE_START');

    this._articleService.getList().subscribe(articles => {
      articles.forEach(article => {
        if (article.adjustment == AdjustmentKind.FixedPercentage) {
          this.excelColumnDefinitions.push(
            this._cdf.createMoney(
              article.code + ' ' + article.caption,
              `fixedPercentageYearRentAgreement.${article.id}.YearRent`,
              ColumnVisibility.optional
            ),
            this._cdf.createNumber(
              article.code + ' ' + increaseText,
              `fixedPercentageYearRentAgreement.${article.id}.FixedPercentageIncrease`,
              ColumnVisibility.optional
            ),
            this._cdf.createDate(
              article.code + ' ' + nextDateText,
              `fixedPercentageYearRentAgreement.${article.id}.NextIncreaseDate`,
              ColumnVisibility.optional
            )
          );
        }
      });
    });
  }

  private addIndexInformationColumns() {
    const tran = this._languageService.getCurrentTranslations();
    const indexClauseText = getTranslation(tran, 'LEASE_AGREEMENT_ROW.INDEX_CLAUSE');
    const indexShareText = getTranslation(tran, 'LEASE_AGREEMENT_ROW.INDEX_SHARE');
    const currentRentText = getTranslation(tran, 'LEASE_AGREEMENT_ROW.INDEX_RENT_CURRENT');
    const baseRentText = getTranslation(tran, 'LEASE_AGREEMENT_ROW.INDEX_RENT_BASE');

    this._articleService.getList().subscribe(articles => {
      articles.forEach(article => {
        if (article.adjustment == AdjustmentKind.Index) {
          this.excelColumnDefinitions.push(
            this._cdf.create(
              article.code + ' ' + indexClauseText,
              `indexInformationAgreement.${article.id}.IndexClause`,
              ColumnVisibility.optional
            ),
            this._cdf.create(
              article.code + ' ' + indexShareText,
              `indexInformationAgreement.${article.id}.IndexShare`,
              ColumnVisibility.optional
            ),
            this._cdf.createNumber(
              article.code + ' ' + baseRentText,
              `indexInformationAgreement.${article.id}.BaseYearRent`,
              ColumnVisibility.optional
            ),
            this._cdf.createNumber(
              article.code + ' ' + currentRentText,
              `indexInformationAgreement.${article.id}.YearRent`,
              ColumnVisibility.optional
            )
          );
        }
      });
    });
  }

  exportExcel(): void {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
    this._subscription = this._dataService.getRentRollExcel(this.form.value).subscribe(file => {
      this._nativeWindow.save(file);
    });
  }

  onPrintPages() {
    if (!canSubmitReportForm(this.form)) return;
    window.print();
  }

  monthSelection: SelectionItem[] = [
    new SelectionItem(getMonthName(1), 1),
    new SelectionItem(getMonthName(2), 2),
    new SelectionItem(getMonthName(3), 3),
    new SelectionItem(getMonthName(4), 4),
    new SelectionItem(getMonthName(5), 5),
    new SelectionItem(getMonthName(6), 6),
    new SelectionItem(getMonthName(7), 7),
    new SelectionItem(getMonthName(8), 8),
    new SelectionItem(getMonthName(9), 9),
    new SelectionItem(getMonthName(10), 10),
    new SelectionItem(getMonthName(11), 11),
    new SelectionItem(getMonthName(12), 12),
  ];

  dataContext: any[] = [];

  form: FormGroup;
  today = new Date();

  private _getPath(category: RentalObjectCategory) {
    let segment = '';
    switch (category) {
      case RentalObjectCategory.Dwelling:
      case RentalObjectCategory.Parking:
      case RentalObjectCategory.Premises:
        segment = RentalObjectCategory[category].toLowerCase();
        break;

      case RentalObjectCategory.Other:
        segment = 'other-rental-object';
        break;

      default:
        throw new Error(`The specified contract category is not supported. Category: ${category}`);
    }
    return segment;
  }

  onRowClicked(e: { context: any; event: any }): void {
    this._navigationService.navigate(
      ['my-property', this._getPath(e.context.rentalObjectCategory), 'edit', e.context.id],
      null,
      e.event.ctrlKey
    );
  }

  private _subscription: Subscription;
  onSubmit() {
    if (!canSubmitReportForm(this.form)) return;
    if (this._subscription) this._subscription.unsubscribe();
    this._subscription = this._dataService.getRentRollReport(this.form.value).subscribe(data => {
      this.dataContext = Array.isArray(data) ? data : [];
    });
  }
}
